<script>
import Icon from '@/components/Icon'

export default {
  name: 'NumberField',

  components: {
    Icon,
  },

  props: {
    placeholder: {
      type: String,
      default: null,
    },

    textColor: {
      type: String,
      default: null,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    value: {
      type: Number,
      default: null,
    },

    min: {
      type: Number,
      default: 0,
    },

    max: {
      type: Number,
      default: null,
    },

    step: {
      type: Number,
      default: null,
    },

    light: {
      type: Boolean,
      default: false,
    },

    // Renderiza botões customizados de −/+ no lugar do spin button nativo.
    // O spinner nativo entra em auto-repeat e não para no "soltar" emulado em
    // dispositivos touch, causando incremento infinito (issue #46); os botões
    // customizados garantem "1 clique = 1 passo" em qualquer dispositivo.
    controls: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    computedClasses () {
      return {
        [[this.$classes['form-input']]]: true,
        [[this.$classes['is-disabled']]]: this.disabled,
        [[this.$classes['is-light']]]: this.light,
        [[this.$classes['has-controls']]]: this.controls,
      }
    },

    // Passo efetivo: usa `step` quando informado, senão 1.
    effectiveStep () {
      return this.step || 1
    },

    // Valor numérico atual; quando vazio/null, ancora no mínimo.
    currentValue () {
      return Number.isFinite(this.value) ? this.value : this.min
    },

    canDecrement () {
      return !this.disabled && !this.readonly && this.currentValue > this.min
    },

    canIncrement () {
      return !this.disabled && !this.readonly &&
        (this.max === null || this.currentValue < this.max)
    },
  },

  methods: {
    // O segundo argumento do `input` indica a origem da alteração ('digitacao'
    // ou 'botao'), para o pai poder tratar cada uma de um jeito (ex.: esperar
    // mais antes de salvar durante a digitação). O v-model usa só o primeiro.
    updateValue (value) {
      this.validation && this.validation.$touch()
      this.$emit('input', parseInt(value || 0), 'digitacao')
    },

    // Aplica o passo respeitando min/max e corrige imprecisão de float.
    commit (value) {
      let next = value
      if (this.max !== null && next > this.max) next = this.max
      if (next < this.min) next = this.min
      next = Math.round(next * 1e6) / 1e6

      this.validation && this.validation.$touch()
      this.$emit('input', next, 'botao')
    },

    // Sair do campo para os botões −/+ do próprio componente não conta como
    // "saiu do campo": o clique no botão já emite a alteração dele.
    handleBlur (event) {
      if (event.relatedTarget && this.$el.contains && this.$el.contains(event.relatedTarget)) return
      this.$emit('blur')
    },

    increment () {
      if (!this.canIncrement) return
      this.commit(this.currentValue + this.effectiveStep)
    },

    decrement () {
      if (!this.canDecrement) return
      this.commit(this.currentValue - this.effectiveStep)
    },
  },
}
</script>

<template>
  <!-- Variante com botões customizados (sem spinner nativo) -->
  <div v-if="controls" :class="$classes['number-control']">
    <button type="button" :class="[$classes['number-control-btn'], $classes['is-minus'], {
      [$classes['is-disabled']]: !canDecrement,
    }]" :disabled="!canDecrement" aria-label="Diminuir quantidade" @click="decrement">
      <icon name="minus" :class="$classes['number-control-icon']" />
    </button>

    <input type="number" :class="computedClasses" :placeholder="placeholder" :disabled="disabled" :readonly="readonly"
      :value="value" :min="min" :max="max" :step="step" :style="{ color: textColor }"
      @input="updateValue($event.target.value)" @blur="handleBlur" @keydown.enter="$emit('submit')" />

    <button type="button" :class="[$classes['number-control-btn'], $classes['is-plus'], {
      [$classes['is-disabled']]: !canIncrement,
    }]" :disabled="!canIncrement" aria-label="Aumentar quantidade" @click="increment">
      <icon name="plus" :class="$classes['number-control-icon']" />
    </button>
  </div>

  <!-- Variante padrão (input nativo) -->
  <input v-else type="number" :class="computedClasses" :placeholder="placeholder" :disabled="disabled"
    :readonly="readonly" :value="value" :min="min" :max="max" :step="step" :style="{ color: textColor }"
    @input="updateValue($event.target.value)" @blur="handleBlur" @keydown.enter="$emit('submit')" />
</template>

<style module>
.form-input {
  border: 1px solid #A1A1A1;
  display: block;
  width: 100%;
  max-width: 100px;
  height: 30px;
  font-size: 12px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", Ubuntu, Cantarell, "Helvetica Neue", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
  color: #292929;
  background: none;
  border-radius: 2px;
  padding: 0 10px;
}

.is-disabled {
  cursor: text;
}

.is-light {
  border-color: #fff;
  background: #fff;
}

/*
 * Em dispositivos com toque disponível (desktop touch ou tela de toque), o
 * spin button nativo entra em auto-repeat e não para no "soltar" emulado,
 * causando incremento infinito (issue #46). Escondemos o spinner só nesses
 * casos via `any-pointer: coarse`; no desktop só-mouse as setas continuam.
 */
@media (any-pointer: coarse) {
  .form-input::-webkit-inner-spin-button,
  .form-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
  }

  .form-input {
    -moz-appearance: textfield;
    appearance: textfield;
  }
}

/*
 * Variante com botões customizados: nunca mostra o spinner nativo, em nenhum
 * dispositivo — quem controla o passo são os botões −/+. O grupo é
 * "frame-agnostic" (sem borda externa própria) pra se encaixar bem tanto
 * sozinho quanto dentro de um container já estilizado (ex.: o
 * .number-field-wrapper da tela de Pedido Detalhes).
 */
.has-controls {
  border: 0;
  background: transparent;
  height: 36px;
  width: 50px;
  max-width: 50px;
  padding: 0 4px;
  text-align: center;
  font-weight: 600;
  color: #1A1A1A;
  -moz-appearance: textfield;
  appearance: textfield;
}

.has-controls::-webkit-inner-spin-button,
.has-controls::-webkit-outer-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

.number-control {
  display: inline-flex;
  align-items: center;
}

.number-control-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #3D3D3D;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease-in-out, color 0.15s ease-in-out;
}

/* Divisores sutis entre os botões e o input (sem moldura externa). */
.number-control-btn.is-minus {
  border-right: 1px solid #E2E2E2;
}

.number-control-btn.is-plus {
  border-left: 1px solid #E2E2E2;
}

.number-control-btn:hover:not(.is-disabled) {
  background: #F8F8F9;
}

.number-control-btn.is-disabled {
  color: #C7C7C7;
  cursor: not-allowed;
}

.number-control-icon {
  width: 12px;
  height: 12px;
  margin: 0;
}
</style>
